import { Injectable } from '@angular/core';
import { EntityService, HttpClient, NumberUtils, PageParameters } from '@elotech/angular-common';
import { Observable, of } from 'rxjs';
import { PortalServidor } from '../common/annotations/portal-servidor';
import { Page } from '../common/types/page.type';
import { FichaFinanceiraEventosDescontoJudicialDTO } from '../desconto-judicial/desconto-judicial.type';
import { CalculadoPago } from '../programacao-ferias/CalculadoPago';

@Injectable()
export class FichaFinanceiraService extends EntityService<any> {

  constructor(public httpClient: HttpClient) {
    super(httpClient);
    this.resource = '/api/ficha-financeira';
  }

  public existsFichaFinanceiraNaCompetenciaLogadaTipoFolhaPrincipalMensalByFuncionarioId(id): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/exists/competencia-logada/tipo-folha-principal-mensal/funcionario/${id}`
    });
  }

  public getCalculoDetalhesFuncionarioProgramacao(funcionarioId, programacaoId): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/funcionario/${funcionarioId}/programacao/${programacaoId}`
    });
  }

  public getCalculoDetalhesPorProgFerias(id): Observable<CalculoFichaFinanceiraDTO> {
    return this.httpClient.get({
      resource: `${this.resource}/programacao-ferias/${id}/detalhes`
    });
  }

  public getDesligamento(id): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/desligamento/${id}/detalhes`
    });
  }

  public isProgramacaoFeriasCalculadaOuPaga(programacaoFeriasId: number): Observable<CalculadoPago> {
    return this.httpClient.get({
      resource: `${this.resource}/programacao-ferias-calculada-ou-paga/${programacaoFeriasId}`
    });
  }

  public getProgramacaoFeriasIdByFichaFinanceira(fichaFinanceiraId: string): Observable<number[]> {
    return this.httpClient.get({
      resource: `${this.resource}/programacao-ferias-id/${fichaFinanceiraId}`
    });
  }

  public getCalculoMensal(competenciaId: number): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/resumo-mensal?competenciaId=${competenciaId}`,
      disableSpinner: true
    });
  }

  public getCalculoMensalDetalhado(competenciaId: number): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/resumo-mensal-detalhado?competenciaId=${competenciaId}`,
      disableSpinner: true
    });
  }

  public isFichaPaga(funcionario, mes, ano): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/isFichaPaga/${funcionario}/${mes}/${ano}`
    });
  }

  public existsByTipoFolhaId(tipoFolhaId): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/exists-ficha-financeira-por-tipo-folha/${tipoFolhaId}`
    });
  }

  @PortalServidor()
  public getUltimosHolerites(id: any, page: PageParameters): Observable<any> {
    return this.httpClient.post({
      resource: `${this.resource}/ultimos-holerites/${id}`,
      page: page,
      disableSpinner: true
    });
  }

  isPeriodoComFichaPaga(funcionarioId: number, dataInicial: any, dataFinal: any, ateLogada: boolean): Observable<boolean> {
    return this.httpClient.post({
      resource: `${this.resource}/exists/ficha-paga/${funcionarioId}${ateLogada ? '?ateLogada=true' : ''}`,
      body: {dataInicial: dataInicial, dataFinal: dataFinal},
      disableSpinner: true
    });
  }

  verificaPreCalculoByMatricula(filtros: any, isExclusao: boolean): Observable<void> {
    return this.httpClient.post({
      resource: `${this.resource}/pre-calculo?isExclusao=${isExclusao}`,
      body: filtros,
      disableSpinner: false
    });
  }

  existeFichaDeCompetenciaAnteriorSendoCalculadaPorMatricula(filtros): Observable<any> {
    const {competencia, matriculas} = filtros;

    const params = {
      mes: competencia.numeroMes,
      exercicio: competencia.ano,
      matriculas: matriculas.length > 0 ? matriculas.split(';') : null
    };

    return this.httpClient.post({
      resource: `${this.resource}/ficha-anterior-na-competencia`,
      body: params
    });
  }

  public findFichaFinanceiraMensalIdPorFuncionarioMesAndAno(funcionario, mes, ano): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/find-ficha-financeira-id/${funcionario}/${mes}/${ano}`
    });
  }

  public isEventoCalculoJaCalculado(eventoId: any, classeId: any): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/evento/${eventoId}/${classeId}`,
      location: this.location
    });
  }

  public podeRemoverByMovimentacao(id: any) {
    return this.httpClient.get({
      resource: `${this.resource}/can-remove/movimentacao/${id}`,
      disableSpinner: true
    });
  }

  public getFichaMensalByFuncionarioCompetencia(funcionario, mes, ano): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/get-ficha-funcionario-mes-ano/${funcionario}/${mes}/${ano}`
    });
  }

  public findAllByFuncionarioCompetencia(funcionario, mes, ano): Observable<CalculoFichaFinanceiraDTO[]> {
    return this.httpClient.get({
      resource: `${this.resource}/all/${funcionario}/${mes}/${ano}`,
      disableSpinner: true,
    });
  }

  public findAllHisitoricoFichaEmAberto(competenciaId, page: PageParameters, search?: string): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/historico-ficha-aberto/${competenciaId}`,
      disableSpinner: true,
      search,
      page
    });
  }

  public findAllHistoricoFichasPagas(competenciaId, page: PageParameters, search?: string): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/historico-ficha-pago/${competenciaId}`,
      disableSpinner: true,
      search,
      page
    });
  }

  public findAllHistoricoFichasEstornadas(competenciaId, page: PageParameters, search?: string): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/historico-ficha-estornada/${competenciaId}`,
      disableSpinner: true,
      search,
      page
    });
  }

  public findFichasPensaoAlimenticia(pensaoAlimenticiaId, page: PageParameters): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/pensao-alimenticia/${pensaoAlimenticiaId}`,
      page
    });
  }

  public findFichaFinanceiraRescisaoIdPorFuncionarioMesAndAno(funcionario, mes, ano): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/find-ficha-financeira-rescisao-id/${funcionario}/${mes}/${ano}`
    });
  }
  
  public findFichaFinanceiraIdPorFuncionarioMesAndAno(funcionario, mes, ano): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/find-ficha-financeira-id/${funcionario}/${mes}/${ano}`
    });
  }

  public findFichasDescontoJudicial(descontoJudicialId: number, page: PageParameters): Observable<Page<FichaFinanceiraEventosDescontoJudicialDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}/desconto-judicial/${descontoJudicialId}`,
      page
    });
  }

  public findQuantidadeByFuncionarioIdAndCompetenciaId(funcionariId: number, competenciaId: number): Observable<number> {
    return this.httpClient.get({
      resource: `${this.resource}/quantidade/${funcionariId}/${competenciaId}`
    });
  }

  public isFichaPagaNaCompetencia(mes, ano): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/is-fichaPaga-competencia/${mes}/${ano}`
    });
  }

  public existsFichaCalculadaComDataIgualOuPosteriorAndQuadroVigencia(dataLimite: Date, vigenciasId: number[]): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/is-ficha-calculada-by-data-and-quadro-vigencia/${dataLimite}?quadroSalarialVigenciaIds=${vigenciasId}`
    });
  }

  public existsFichaPagaComQuadroSalarialVigenciaId(quadroSalarialVigenciaId: number): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/is-fichaPaga-by-quadroSalarialvigenciaId/${quadroSalarialVigenciaId}`
    });
  }

  public existsByMediaEventoId(mediaEventoId: number): Observable<boolean> {
    if (!NumberUtils.isNumeric(mediaEventoId)) {
      return of(false);
    }

    return this.httpClient.get({
      resource: `${this.resource}/exists-by-media-evento/${mediaEventoId}`
    });
  }

  public existsPagaByCompetenciaAndDefinicao(competenciaId: number, definicao: string, matriculas: number[]): Observable<boolean> {
    return this.httpClient.post({
      resource: `${this.resource}/exists-ficha-paga-by-competencia-definicao?competenciaId=${competenciaId}&definicao=${definicao}`,
      body: matriculas
    });
  }
}

interface TipoFolhaDTO {
  id: number;
  codigo: number;
  descricao: string;
  especie: any;
  referencia: any;
  integral: boolean;
  entidade: any;
}

export interface CalculoFichaFinanceiraDTO {
  calculoId: string;
  funcionario: any;
  eventos: any[];
  competenciaFormatada: string;
  competencia: any;
  tipoFolha: string;
  tipoFolhaDTO: TipoFolhaDTO;
  programacaoFeriasId: number;
  proventos: number;
  descontos: number;
  liquido: number;
  previous: any;
  next: any;
  dataPagamento: string;
  movimentacaoDesligamentoId: number;
  idFuncionarioParcela: number;
  dataPagamentoFormatada: string;
}
